import { languageOptions, MARKETPLACE_AMAZON_TO_MARKETPLACE } from '@amzn/journeys-common';
import { Alert, Column, Link, Row, Text } from '@amzn/meridian';
import { shouldDisableAIGeneration } from 'components/AltText/utils/imageSizeValidation';
import { ErrorAlert } from 'components/ErrorAlert';
import { FormLabelWithOptional } from 'components/form/FormLabelWithOptional';
import { getLanguageOptionFromDisplayLanguage } from 'features/campaign/campaign.util';
import { useFlavorDisplayLanguage } from 'features/traffic/campaigns/useFlavor';
import type { AssetModel } from 'generated/graphql';
import { useSpyOnFieldValue } from 'hooks/form-spy/useSpyOnFieldValue';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useField, useForm, useFormState } from 'react-final-form';
import { usePrevious } from 'react-use';
import { AltTextField, AltTextMode } from '../../AltTextField';
import { GenerateAltTextButton } from '../../components/GenerateAltTextButton';
import { MAX_IMAGE_SIZE_BYTES } from '../../constants';
import type { DecorAltTextWithGenAIFieldProps, MCCAltTextWithGenAIFieldProps, SharedAltTextWithGenAIFieldProps } from '../types';

export const MCCAltText = <TContent extends Record<string, unknown> = Record<string, unknown>>(
  props: MCCAltTextWithGenAIFieldProps<TContent>,
) => {
  const { imageFieldPath, altTextFieldPath, labelName, isDecorative, mode, flavorIndex } = props;

  const { sharedWrapperName, sharedWrapperClassName, sharedWrapperValidator, sharedWrapperConfig } =
    props as SharedAltTextWithGenAIFieldProps<TContent>;

  const { decorativeFieldName } = props as DecorAltTextWithGenAIFieldProps;

  const assetValueSpy: AssetModel | null | undefined = useSpyOnFieldValue({
    fieldPath: imageFieldPath,
  });
  const assetId = useMemo(
    () => assetValueSpy?.assetId ?? undefined,
    [
      assetValueSpy,
    ],
  );
  const imageSize = assetValueSpy?.size ?? 0;

  const imageType = useMemo(() => {
    if (!assetValueSpy?.name) return null;

    const fileExtension = assetValueSpy.name.toLowerCase().split('.').pop();
    return fileExtension || null;
  }, [
    assetValueSpy,
  ]);

  const displayLanguage = useFlavorDisplayLanguage();
  const defaultOption = useMemo(
    () => getLanguageOptionFromDisplayLanguage(displayLanguage),
    [
      displayLanguage,
    ],
  );

  const isSharedMode = (m: AltTextMode) => m === AltTextMode.Shared || m === AltTextMode.SharedAndDecorative;

  if (flavorIndex != null) {
    if (typeof flavorIndex !== 'number' || flavorIndex < 0) {
      throw new Error('FlavorIndex must be a non-negative number');
    }
  }

  const { values } = useFormState<{
    flavors?: Array<{
      scheduling?: {
        language: string;
        marketplace: string;
      };
    }>;
  }>({
    subscription: {
      values: flavorIndex != null,
    },
  });

  const schedulingValue = flavorIndex != null ? values?.flavors?.[flavorIndex]?.scheduling : undefined;
  const selectedLanguage = schedulingValue?.language ?? null;
  const selectedMarketplace = schedulingValue?.marketplace ?? null;

  const flavorOption = useMemo(
    () =>
      languageOptions.find(
        (opt) =>
          opt.language === selectedLanguage &&
          MARKETPLACE_AMAZON_TO_MARKETPLACE[opt.marketplace] != null &&
          MARKETPLACE_AMAZON_TO_MARKETPLACE[opt.marketplace] === selectedMarketplace,
      ) ?? null,
    [
      selectedLanguage,
      selectedMarketplace,
    ],
  );

  const languageOption = flavorIndex != null ? flavorOption : defaultOption;
  const languageCode = languageOption?.language ?? null;

  const [suggestion, setSuggestion] = useState<string>('');
  const [errorObj, setErrorObj] = useState<Error | null>(null);

  const disableGenerate = shouldDisableAIGeneration(imageSize, imageType, MAX_IMAGE_SIZE_BYTES);

  const altTextField = useField<string>(altTextFieldPath, {
    subscription: {
      error: true,
      touched: true,
    },
  });

  const form = useForm();
  const previousDecorative = usePrevious(isDecorative);

  useEffect(() => {
    void assetId;
    void languageCode;
    void isDecorative;
    setSuggestion('');
    setErrorObj(null);
  }, [
    assetId,
    languageCode,
    isDecorative,
  ]);

  useEffect(() => {
    if (isDecorative && isDecorative !== previousDecorative) {
      form.change(altTextFieldPath, '');
    }
  }, [
    isDecorative,
    previousDecorative,
    form,
    altTextFieldPath,
  ]);

  useEffect(() => {
    form.blur(altTextFieldPath);
  }, [
    altTextFieldPath,
    form,
  ]);

  const clearError = () => {
    setErrorObj(null);
  };

  const handleUseRef = useRef<() => void>(() => {});

  const [savedDeviceName, setSavedDeviceName] = useState('');

  const allowedModes = Object.values(AltTextMode) as AltTextMode[];
  if (!allowedModes.includes(mode)) {
    throw new Error(`Invalid mode: ${mode}`);
  }

  const altTextFieldProps = {
    mode,
    name: altTextFieldPath,
    ...((mode === AltTextMode.Decorative || mode === AltTextMode.SharedAndDecorative) && {
      decorativeFieldName,
    }),
    disableField: isDecorative,
    ...(isSharedMode(mode) && {
      sharedWrapperClassName,
      sharedWrapperConfig,
      sharedWrapperName,
      sharedWrapperValidator,
    }),
    clearError,
    clearSuggestion: () => setSuggestion(''),
    handleUseRef,
    suggestion,
  };

  return (
    <Column spacing="200">
      <Row alignmentHorizontal="justify" alignmentVertical="bottom">
        <FormLabelWithOptional label={labelName} />
        {!isDecorative && (
          <GenerateAltTextButton
            assetId={assetId}
            disableGenerate={disableGenerate}
            imageType={imageType}
            locale={languageCode}
            onAltTextGenerated={(text) => {
              setSuggestion(text);
              clearError();
            }}
            onError={(err) => {
              setErrorObj(err);
            }}
            onNameChange={setSavedDeviceName}
            onSubmit={() => {
              setSuggestion('');
              clearError();
            }}
            productName={savedDeviceName}
          />
        )}
      </Row>
      <Row
        alignmentVertical="center"
        widths={[
          '100%',
          'fill',
        ]}
      >
        <AltTextField {...altTextFieldProps} />
      </Row>
      <Row
        alignmentVertical="center"
        widths={[
          'fill',
          'auto',
        ]}
      >
        {!isDecorative &&
          (errorObj ? (
            <ErrorAlert contextMessage="Alt text generation failed" error={errorObj} />
          ) : (
            altTextField.meta.touched &&
            altTextField.meta.error && (
              <Alert aria-live="polite" size="small" type="error">
                {altTextField.meta.error}
              </Alert>
            )
          ))}
      </Row>
      {!isDecorative && disableGenerate && (
        <Row
          alignmentVertical="center"
          widths={[
            'fill',
            'auto',
          ]}
        >
          <Alert aria-live="polite" size="small" type="warning">
            {imageType?.toUpperCase() || 'Large'} images must be under 4 MB for auto-generation
          </Alert>
        </Row>
      )}
      {!isDecorative && (
        <Row
          alignmentVertical="center"
          widths={[
            'fill',
            'auto',
          ]}
        >
          {suggestion && (
            <Text aria-live="polite" color="secondary" type="b200">
              Auto‑generated: "{suggestion}"{' '}
              <Link aria-label="Use suggested alt text" onClick={() => handleUseRef.current?.()}>
                Use
              </Link>
            </Text>
          )}
        </Row>
      )}
    </Column>
  );
};
